/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { IInbound, IInboundItem } from '@/interfaces/inbound';
import { Checkbox, DatePicker, Form, Input, InputNumber, Table } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { InboundMenu } from '@/configs/menuItems';
import { useQuery } from '@tanstack/react-query';
import { getInbound } from '@/adapters/inbound';
import InboundTable from '@/containers/inbound/components/inbound-table';
import locale from 'antd/es/date-picker/locale/th_TH';
import { AxiosResponse } from 'axios';
import SubMenu from '@/components/menu/sub-menu';
import { getPathSegment, getQueryParam } from '@/utils/pathUtils';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import {
  DeleteFilled,
  CalendarOutlined,
  CopyOutlined,
  EditFilled,
  UploadOutlined,
} from '@ant-design/icons';
import { twMerge } from 'tailwind-merge';
import { mediaView } from '@/utils/mediaUtils';
import { IDataModal } from '@/interfaces/dataModal';
import { IImportData } from '@/interfaces/import';
import MediaUploader from '@/components/media/uploader-modal';

interface EditableCellProps extends React.HTMLAttributes<any> {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: 'number' | 'text' | 'checkbox';
  record: Partial<IInboundItem>;
  index: number;
}
const _importModal: IDataModal<IImportData> = {
  open: false,
};

const GoodReceiptCreate = () => {
  const navigate = useNavigate();
  const pathSegment = getPathSegment();
  const inboundId = getQueryParam('inboundId');
  const [importModal, setImportModal] =
    useState<IDataModal<IImportData>>(_importModal);
  // const navigate = useNavigate();
  const [form] = Form.useForm();
  const [data, setData] = useState<Partial<IInboundItem>[]>([]);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingDateKey, setEditingDateKey] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<string>('');
  const isEditing = (record: Partial<IInboundItem>) =>
    record.key === editingKey;

  const edit = (record: Partial<IInboundItem>) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key || '');
  };

  const save = async (key: string) => {
    try {
      const row = (await form.validateFields()) as Partial<IInboundItem>;
      const newData = [...data];
      const index = newData.findIndex(item => key === item.key || '');
      if (index > -1) {
        const item = newData[index];
        item.coa = `${item?.product?.code}-${row.lotNumber || ''}`;
        newData.splice(index, 1, {
          ...item,

          // unitPrice: round(Number(item.unitPrice || 0), 2),
          // quantity: round(Number(item.quantity || 0), 2),
          ...row,
        });

        setData(newData);
        setEditingKey('');
        setEditingIndex('');
      } else {
        newData.push(row);
        setData(newData);
        setEditingIndex('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const onUpdateCOA = (id: number) => {
    setData(prev =>
      prev.map(item =>
        item.key == importModal.optional ? { ...item, coaId: id } : item,
      ),
    );
    setImportModal({
      ..._importModal,
    });
  };

  const onRowClick = (record: Partial<IInboundItem>, index: string) => {
    edit(record);
    setEditingIndex(index);
  };

  const {
    data: inbound,
    isLoading,
    isFetching,
  } = useQuery<AxiosResponse<IInbound>, Error>({
    queryKey: ['create-gr'],
    queryFn: async () => await getInbound(Number(inboundId)),
    enabled: !!inboundId,
    refetchOnWindowFocus: false,
  });

  const toggleDatePicker = (key: string) => {
    setEditingDateKey(key);
  };

  const onExpiredDateChange = (key: string, date: Date | string | null) => {
    setData(prev =>
      prev.map(item =>
        item.key == key ? { ...item, expiredDate: date } : item,
      ),
    );
  };

  const onCopyItem = (item: IInboundItem) => {
    setData(prev => [...prev, { ...item, key: uuidv4() }]);
  };

  const onDeleteItem = (key: string) => {
    setData(prev => prev.filter(p => p.key != key));
  };

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    record,
    editing,
    dataIndex,
    title,
    inputType,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === 'checkbox' ? (
        <Checkbox onBlur={() => save(record.key || '')}>{title}</Checkbox>
      ) : inputType === 'number' ? (
        <InputNumber
          className="w-full"
          onBlur={() => save(record.key || '')}
          onPressEnter={() => save(record.key || '')}
          autoFocus
          controls={false}
          step={record?.product?.conversion || 1}
          decimalSeparator={'.'}
          precision={2}
          max={Number(record._totalQty) - Number(record.totalReceivedQty || 0)}
          style={{ textAlignLast: 'right' }}
        />
      ) : (
        <Input
          autoFocus
          onBlur={() => save(record.key || '')}
          onPressEnter={() => save(record.key || '')}
        />
      );

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            valuePropName={inputType === 'checkbox' ? 'checked' : undefined}
            rules={[
              {
                required: dataIndex === 'lotNumber',
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: '',
      width: 40,
      dataIndex: 'action',
      align: 'center',
      render: (_: string, rec: IInboundItem) => (
        <div className="flex justify-center gap-2">
          <CopyOutlined
            className="text-blue-700"
            onClick={() => onCopyItem(rec)}
          />
        </div>
      ),
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 120,
      className: 'font-bold',
      align: 'center',
      render: (_: any, rec: IInboundItem) => (
        <div>{rec?.product?.code || ''}</div>
      ),
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
      render: (_: any, rec: IInboundItem) => (
        <div>{rec?.product?.name || ''}</div>
      ),
    },
    {
      editable: editingIndex === 'lotNumber',
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 140,
      className: 'font-bold',
      align: 'center',
      render: (_: string, rec: IInboundItem) => (
        <div
          className={twMerge('border px-1 rounded', !_ && 'text-red-500')}
          onClick={() => onRowClick(rec, 'lotNumber')}
        >
          {_ || '** Lot Number'}
        </div>
      ),
    },
    {
      title: 'COA',
      dataIndex: 'coa',
      width: 240,
      className: 'font-bold',
      align: 'center',
      render: (_: string, rec: IInboundItem) => (
        <div className="flex flex-row gap-3 cursor-pointer justify-between">
          <div
            className={twMerge(rec.coaId && 'text-blue-600 underline')}
            onClick={() => (rec.coaId ? mediaView({ id: rec.coaId }) : false)}
          >
            {_ ?? 'ไม่พบไฟล์ COA'}
          </div>
          <div
            className="font-bold"
            onClick={() =>
              setImportModal({
                open: true,
                id: rec.id,
                optional: rec.key,
              })
            }
          >
            <UploadOutlined />
          </div>
        </div>
      ),
    },
    {
      title: 'ExpiredDate',
      dataIndex: 'expiredDate',
      width: 140,
      className: 'font-bold',
      align: 'center',
      render: (_: any, rec: IInboundItem) => (
        <div className="flex justify-between">
          <div className={twMerge(!_ && 'text-red-500')}>
            {(_ && formatDate(_)) || '**expired date'}
          </div>
          <div>
            <CalendarOutlined onClick={() => toggleDatePicker(rec.key || '')} />
            <DatePicker
              open={rec.key === editingDateKey}
              onOpenChange={() => setEditingDateKey('')}
              inputReadOnly
              style={{
                opacity: 0, // Make the input invisible
                height: 0, // Remove the visual height of the input
                pointerEvents: 'none', // Prevent interaction with the input
                position: 'absolute', // Keep the input in the flow for positioning
              }}
              locale={locale}
              onChange={date =>
                onExpiredDateChange(rec.key || '', date?.toDate())
              }
              // getPopupContainer={trigger => trigger.parentElement!}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Conversion',
      width: 100,
      dataIndex: 'conversion',
      align: 'center',
      render: (_: number, rec: IInboundItem) =>
        formatNumber(rec?.product?.conversion),
    },
    {
      title: 'Package',
      width: 100,
      dataIndex: 'package',
      className: 'text-blue-700 font-bold',
      align: 'center',
      render: (_: number, rec: IInboundItem) =>
        formatNumber(
          Number(rec?.totalQty || 0) / Number(rec?.product?.conversion || 0),
          2,
        ),
    },
    {
      title: 'UOM',
      width: 100,
      align: 'center',
      dataIndex: 'packageUnitName',
      render: (_: number, rec: IInboundItem) => (
        <div>{rec?.product?.packageUnitId}</div>
      ),
    },
    {
      title: 'จำนวน',
      width: 120,
      editable: editingIndex === 'totalQty',
      dataIndex: 'totalQty',
      className: 'font-bold text-green-700',
      align: 'right',
      render: (_: number, rec: IInboundItem) => (
        <div
          className="font-bold w-full cursor-pointer border px-1 rounded"
          onClick={() => onRowClick(rec, 'totalQty')}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: 'ตัวอย่าง',
      width: 80,
      dataIndex: 'ISample',
      className: 'font-bold',
      align: 'center',
      editable: editingIndex === 'ISample',
      render: (_: number, rec: IInboundItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-between"
          onClick={() => onRowClick(rec, 'ISample')}
        >
          {(_ && 'ตย.') || '-'}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },
    {
      editable: editingIndex === 'remark',
      title: 'Remark',
      dataIndex: 'remark',
      width: 250,
      // className: 'font-bold',
      render: (_: string, rec: IInboundItem) => (
        <div
          className={twMerge(!_ && 'text-gray-400')}
          onClick={() => onRowClick(rec, 'remark')}
        >
          {_ || 'หมายเหตุ'}
        </div>
      ),
    },
    {
      title: '',
      width: 40,
      dataIndex: 'action',
      align: 'center',
      render: (_: string, rec: IInboundItem) => (
        <div className="flex justify-center gap-2">
          <DeleteFilled
            className="text-red-700"
            onClick={() => onDeleteItem(rec.key || '')}
          />
        </div>
      ),
    },
  ];

  const mergedColumns: any = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IInboundItem) => ({
        record,
        inputType:
          editingIndex === 'ISample'
            ? 'checkbox'
            : ['totalQty'].includes(editingIndex)
              ? 'number'
              : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  useEffect(() => {
    if (inbound?.data?.inboundItems) {
      const inboundItems =
        inbound?.data?.inboundItems?.map(i => {
          i._totalQty = i.totalQty;
          i.coa = `${i?.product?.code || ''}-${i.lotNumber || ''}`;
          i.key = uuidv4();
          return i;
        }) || [];
      setData(inboundItems || []);
    }
  }, [inbound]);

  console.log(data);

  return (
    <>
      <SubMenu active={pathSegment} items={InboundMenu} />
      <Card>
        <BreadcrumbComponent
          items={[
            {
              title: (
                <div
                  className="cursor-pointer"
                  onClick={() => navigate(`/order`)}
                >
                  Good Receipt
                </div>
              ),
            },
            {
              title: 'สร้าง',
            },
          ]}
        />
        <div className="flex flex-col gap-3">
          <Title
            customClassName="text-lg text-blue-700 mt-4"
            customContainerClassName="py-0"
            title={`Inbound`}
          />
          <InboundTable
            criteria={{ pageSize: 10, pageStart: 1 }}
            inbound={{
              data: (inbound?.data && [inbound?.data]) || [],
              totalRecords: 0,
            }}
            loading={isLoading || isFetching}
            onEditData={item => console.log(item)}
            onDelete={() => undefined}
            onTableChange={() => undefined}
            showPagging={false}
          />
          <div className="flex flex-col w-full gap-3">
            <Title
              customClassName="text-lg text-blue-700 mt-4"
              customContainerClassName="py-0"
              title={`รายการ`}
            />
            <Form form={form} component={false}>
              <Table
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                locale={{
                  emptyText: 'เพิ่มรายการใหม่',
                }}
                size="middle"
                bordered
                dataSource={data || []}
                columns={mergedColumns}
                className="w-full"
                rowClassName="editable-row"
                pagination={false}
                scroll={{ x: 1200 }}
              />
            </Form>
            <MediaUploader
              data={{
                modelId: Number(importModal?.id),
                modelType: 'coa',
              }}
              open={importModal.open}
              onCancel={() => {
                setImportModal({
                  ..._importModal,
                });
              }}
              onUploaded={id => {
                onUpdateCOA(Number(id || 0));
              }}
            />
          </div>
        </div>
      </Card>
    </>
  );
};

export default GoodReceiptCreate;
